/**
 * StructuralExtractionModal — define (or view) a tabular extraction scope for CSV/Excel/TXT documents.
 *
 * Workflow (create mode):
 *   1. Open modal → load Parquet preview (250 rows) via getTabularPreview
 *   2. Left column: QueryBuilder + TabularPreviewTable
 *   3. Right column: Header config, Data Selection summary, Actions (case + save)
 *   4. User optionally sets header row (click preview row in header-select mode)
 *   5. User selects a rectangular region by clicking/dragging cells in build-batch mode
 *   6. User saves scope via extractionScopeApi.create with scope_type='all'
 *
 * Workflow (readOnly mode):
 *   1. Open modal → fetch saved scope via extractionScopeApi.getByCase
 *   2. Restore pendingRows / pendingColumns / headerNames from tabular_config
 *   3. Load Parquet preview so the table renders with the saved selection highlighted
 *   4. Split-pane layout: SchemaTab (top) + ScopeTab data preview (bottom)
 *   5. Inline header: Mark as Reviewed, Save Schema, Close
 *
 * This component is a thin presentational orchestrator: all IO/state lives in
 * `useStructuralExtraction`; the header, create-mode panel, read-only sidebar and
 * empty state are pure components. `SchemaTab` / `GeoMappingTab` / `ScopeTab` are
 * pre-existing connected sections wired here from the hook's data + callbacks.
 */

import { useMemo } from 'react'
import '@xyflow/react/dist/style.css'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useStructuralExtraction } from '@/features/lake/hooks/useStructuralExtraction'
import type { LakeDocument } from '@/services/api/lake'
import { useTheme } from '@/theme'
import { EntityNode } from './extraction-modal/EntityNode'
import { GeoMappingTab } from './extraction-modal/GeoMappingTab'
import { SchemaTab } from './extraction-modal/SchemaTab'
import { ScopeTab } from './extraction-modal/ScopeTab'
import { CreateScopePanel } from './structural-extraction/CreateScopePanel'
import { ParquetUnavailable } from './structural-extraction/ParquetUnavailable'
import { ReadOnlyDataSidebar } from './structural-extraction/ReadOnlyDataSidebar'
import { StructuralExtractionHeader } from './structural-extraction/StructuralExtractionHeader'

// ---------------------------------------------------------------------------
// Node types for ReactFlow
// ---------------------------------------------------------------------------

const nodeTypes = {
  entityNode: EntityNode,
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface StructuralExtractionModalProps {
  document: LakeDocument
  open: boolean
  onClose: () => void
  onSaved: () => void
  /** When true, the modal is a read-only viewer for an existing scope. */
  readOnly?: boolean
  /**
   * The case ID whose scope should be displayed when readOnly=true.
   * The modal fetches the scope itself via extractionScopeApi.getByCase.
   */
  preSelectedCaseId?: string
  /**
   * Kept for API compatibility but ignored — readOnly mode no longer uses tabs.
   */
  defaultTab?: 'scope' | 'schema'
}

export function StructuralExtractionModal({
  document,
  open,
  onClose,
  onSaved,
  readOnly = false,
  preSelectedCaseId,
}: StructuralExtractionModalProps) {
  // Theme support
  const { theme: themeRaw } = useTheme()
  const isDark = useMemo(() => {
    if (themeRaw === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return themeRaw === 'dark'
  }, [themeRaw])

  const s = useStructuralExtraction({
    document,
    open,
    onClose,
    onSaved,
    readOnly,
    preSelectedCaseId,
    isDark,
  })

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="w-[98vw] max-w-none sm:max-w-none h-[96dvh] p-0 flex flex-col gap-0 overflow-hidden md:h-[96vh]"
      >
        <StructuralExtractionHeader
          filename={document.filename}
          readOnly={readOnly}
          isGeospatial={s.isGeospatial}
          reviewed={!!s.loadedScope?.reviewed_at}
          marking={s.marking}
          markDisabled={
            s.marking ||
            !s.loadedScope ||
            (s.isGeospatial ? !s.loadedScope.manual_schema : s.entities.length === 0)
          }
          onMarkReviewed={s.handleSaveAndMarkReviewed}
          onClose={onClose}
        />

        {/* Body */}
        {!s.hasParquet ? (
          <ParquetUnavailable />
        ) : readOnly ? (
          /* ----------------------------------------------------------------
              READ-ONLY MODE: Split-pane layout
              Top: SchemaTab (schema builder)
              Drag handle
              Bottom: ScopeTab data preview with read-only data sidebar
          ---------------------------------------------------------------- */
          <div className="flex-1 flex flex-col overflow-hidden" data-split-container>
            {/* Top pane — schema builder */}
            <div
              style={{ height: `${s.splitPercent}%` }}
              className="flex flex-col overflow-hidden min-h-[200px]"
            >
              {s.isGeospatial && s.loadedScope?.manual_schema ? (
                <GeoMappingTab
                  wizardConfig={s.loadedScope.manual_schema}
                  availableColumns={s.availableColumns}
                  tabularData={s.tabularData}
                  onConfigChange={updated => {
                    s.setLoadedScope(prev => (prev ? { ...prev, manual_schema: updated } : prev))
                  }}
                />
              ) : (
                <SchemaTab
                  loadingSchema={s.loadingSchema}
                  schema={s.schema}
                  entities={s.entities}
                  relationships={s.relationships}
                  selectedEntityId={s.selectedEntityId}
                  selectedRelationshipId={s.selectedRelationshipId}
                  rightPanelVisible={s.rightPanelVisible}
                  nodes={s.nodes}
                  edges={s.edges}
                  nodeTypes={nodeTypes}
                  onNodesChange={s.onNodesChange}
                  onEdgesChange={s.onEdgesChange}
                  availableColumns={s.availableColumns}
                  tabularData={s.tabularData}
                  onAddEntity={s.handleAddEntity}
                  onDeleteEntity={s.handleDeleteEntity}
                  onCreateRelationship={s.handleCreateRelationship}
                  onNodeClick={s.handleNodeClick}
                  onEdgeClick={s.handleEdgeClick}
                  onSetSelectedEntityId={s.setSelectedEntityId}
                  onSetRightPanelVisible={s.setRightPanelVisible}
                  onPropertyMapping={s.handlePropertyMapping}
                  onSetIdentifier={s.handleSetIdentifier}
                  onUpdateRelationship={s.handleUpdateRelationship}
                  onDeleteRelationship={s.handleDeleteRelationship}
                  pickingContext={s.pickingContext}
                  onStartPickingColumn={s.handleStartPickingColumn}
                  updateLocalPropertyMappingRef={s.updateLocalPropertyMappingRef}
                  updateLocalRowConditionRef={s.updateLocalRowConditionRef}
                  onStartPickingRowConditionColumn={s.handleStartPickingRowConditionColumn}
                  onStartPickingRowConditionValue={s.handleStartPickingRowConditionValue}
                  sidebarWidth={s.sidebarWidth}
                  onSidebarResize={s.handleSidebarDragStart}
                  saveCurrentEntityRef={s.saveCurrentEntityRef}
                  onAutoLayout={s.handleAutoLayout}
                />
              )}
            </div>

            {/* Drag handle */}
            <div
              className="h-1.5 bg-border hover:bg-primary/40 cursor-row-resize shrink-0 transition-colors"
              onMouseDown={s.handleDragStart}
            />

            {/* Bottom pane — data preview */}
            <div className="flex-1 flex overflow-hidden min-h-[150px]">
              <ScopeTab
                ref={s.tableContainerRef}
                columns={s.columns}
                rows={s.rows}
                totalRows={s.totalRows}
                currentOffset={s.currentOffset}
                isLoading={s.isLoading}
                isNavigating={s.isNavigating}
                queryError={s.queryError}
                readOnly={true}
                selectionMode={s.selectionMode}
                headerRowIndex={s.headerRowIndex}
                pendingRows={s.pendingRows}
                pendingColumns={s.pendingColumns}
                onQuery={s.handleQuery}
                onPrevPage={s.handlePrevPage}
                onNextPage={s.handleNextPage}
                onRowClick={s.handleRowClick}
                onColumnClick={s.handleColumnClick}
                onDragSelect={undefined}
                onSetSelectionMode={s.setSelectionMode}
                onClearSelection={s.clearSelection}
                renderRightPanel={() => (
                  <ReadOnlyDataSidebar
                    headerNames={s.headerNames}
                    headerRowIndex={s.headerRowIndex}
                    scopeLoadError={s.scopeLoadError}
                    pendingRows={s.pendingRows}
                    pendingColumns={s.pendingColumns}
                    dataCols={s.dataCols}
                    readOnlyCaseName={s.readOnlyCaseName}
                    scopeNote={s.scopeNote}
                  />
                )}
                columnPickingMode={
                  s.pickingContext !== null && s.pickingContext.type !== 'row-condition-value'
                }
                cellPickingMode={
                  s.pickingContext !== null && s.pickingContext.type === 'row-condition-value'
                }
                onColumnPicked={s.handleColumnPicked}
                onCellPicked={s.handleCellPicked}
                pickingContext={s.pickingContext}
              />
            </div>
          </div>
        ) : (
          /* ----------------------------------------------------------------
              CREATE MODE: QueryBuilder + TabularPreviewTable + Right Panel
          ---------------------------------------------------------------- */
          <ScopeTab
            columns={s.columns}
            rows={s.rows}
            totalRows={s.totalRows}
            currentOffset={s.currentOffset}
            isLoading={s.isLoading}
            isNavigating={s.isNavigating}
            queryError={s.queryError}
            readOnly={false}
            selectionMode={s.selectionMode}
            headerRowIndex={s.headerRowIndex}
            pendingRows={s.pendingRows}
            pendingColumns={s.pendingColumns}
            onQuery={s.handleQuery}
            onPrevPage={s.handlePrevPage}
            onNextPage={s.handleNextPage}
            onRowClick={s.handleRowClick}
            onColumnClick={s.handleColumnClick}
            onDragSelect={s.handleDragSelect}
            onSetSelectionMode={s.setSelectionMode}
            onClearSelection={s.clearSelection}
            renderRightPanel={() => (
              <CreateScopePanel
                header={{
                  headerSource: s.headerSource,
                  headerRowIndex: s.headerRowIndex,
                  headerNames: s.headerNames,
                  dataCols: s.dataCols,
                  emptyHeaders: s.emptyHeaders,
                }}
                selection={{
                  pendingRows: s.pendingRows,
                  pendingColumns: s.pendingColumns,
                  noSelection: s.noSelection,
                }}
                save={{
                  cases: s.cases,
                  selectedCaseId: s.selectedCaseId,
                  scopeNote: s.scopeNote,
                  saving: s.saving,
                  canSave: s.canSave,
                  noHeaders: s.noHeaders,
                  noCaseSelected: s.noCaseSelected,
                }}
                onHeaderSourceChange={s.setHeaderSource}
                onClearHeaderRow={() => {
                  s.setHeaderRowIndex(null)
                  s.setHeaderNames(s.dataCols.map(c => c.name))
                }}
                onHeaderNameChange={(index, value) => {
                  const next = [...s.headerNames]
                  next[index] = value
                  s.setHeaderNames(next)
                }}
                onSelectedCaseIdChange={s.setSelectedCaseId}
                onScopeNoteChange={s.setScopeNote}
                onSave={s.handleSave}
              />
            )}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
